/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import axios from "../lib/axios";
import { useSidebar } from "../context/SidebarContext";
import { Layers,Sparkles } from "lucide-react";


const ISIGHT_BASE_URL = "https://isight.netsights.ai";
/** Synced with Settings "isight intelligence" (`ai_intelligence`); used for instant UI after save */
export const AI_INTELLIGENCE_STORAGE_KEY = "netsight_ai_intelligence_enabled";

/** Global theme key written by ThemeContext; carried cross-project into iSight via URL. */
const THEME_STORAGE_KEY = "netsight_theme";

/** Read the current user-selected theme so iSight opens in the same dark/light mode. */
function getCurrentTheme(): "light" | "dark" {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
    // Fallback to the <html> class ThemeContext applies before first paint
    if (document.documentElement.classList.contains("dark")) return "dark";
  } catch {
    /* ignore */
  }
  return "dark";
}

function isAiIntelligenceTruthy(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const s = String(value).toLowerCase();
  if (s === "0" || s === "false" || s === "off" || s === "no") return false;
  return true;
}

type IsightOpenButtonVariant = "header" | "sidebar";

interface IsightOpenButtonProps {
  variant?: IsightOpenButtonVariant;
  /** When true, sidebar iSight control is visible but non-interactive (e.g. billing) */
  navigationDisabled?: boolean;
  /** When provided, render this text label instead of the brand wordmark. */
  label?: string;
}

/**
 * Reusable iSight Intelligence opener.
 * This version uses a direct link to open instantly in a new tab,
 * bypassing CORS issues and removing the "Connecting..." delay.
 */
export function IsightOpenButton({ variant, navigationDisabled = false, label }: IsightOpenButtonProps) {
  const { currentShop, isFreePlan } = useAuth();
  const { isExpanded, isHovered, isMobileOpen } = useSidebar();
  const expanded = isExpanded || isHovered || isMobileOpen;

  const [isightError, setIsightError] = useState<string | null>(null);
  const [sightsEnabled, setSightsEnabled] = useState<boolean>(true);
  const [aiIntelligenceEnabled, setAiIntelligenceEnabled] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(AI_INTELLIGENCE_STORAGE_KEY);
      if (raw === null) return true;
      return raw === "1" || raw === "true";
    } catch {
      return true;
    }
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Load interface settings to check sights_enabled
  useEffect(() => {
    const loadInterfaceSettings = async () => {
      try {
        const response = await axios.get("/user/interface-settings");
        const settings = response.data.settings || {};
        setSightsEnabled(settings.sights_enabled !== false); // Default to true
      } catch (error) {
        console.error("Failed to load interface settings:", error);
        // Default to enabled if there's an error
        setSightsEnabled(true);
      }
    };

    loadInterfaceSettings();

    // Listen for iSight setting changes
    const handleSightsChanged = (event: CustomEvent) => {
      setSightsEnabled(event.detail);
    };

    window.addEventListener('sightsEnabledChanged', handleSightsChanged as EventListener);

    // Cleanup event listener
    return () => {
      window.removeEventListener('sightsEnabledChanged', handleSightsChanged as EventListener);
    };
  }, []);
  const [loading, setLoading] = useState(false);

  // "isight intelligence" (ai_intelligence) from advanced settings - controls sidebar/header iSight entry
  useEffect(() => {
    const shop = currentShop || localStorage.getItem("shop");

    const loadAiIntelligence = async () => {
      try {
        const response = await axios.get("/user/advanced-settings", {
          params: shop ? { shop } : {},
        });
        const db = response.data?.settings;
        if (db && response.data?.success !== false) {
          const on = isAiIntelligenceTruthy(db.ai_intelligence);
          setAiIntelligenceEnabled(on);
          try {
            localStorage.setItem(AI_INTELLIGENCE_STORAGE_KEY, on ? "1" : "0");
          } catch {
            /* ignore */
          }
        }
      } catch (error) {
        console.error("Failed to load ai_intelligence from advanced settings:", error);
      }
    };

    loadAiIntelligence();

    const handleAiIntelligenceChanged = (event: CustomEvent<boolean | { enabled?: boolean }>) => {
      const d = event.detail;
      const on = typeof d === "boolean" ? d : !!d?.enabled;
      setAiIntelligenceEnabled(on);
      try {
        localStorage.setItem(AI_INTELLIGENCE_STORAGE_KEY, on ? "1" : "0");
      } catch {
        /* ignore */
      }
    };

    window.addEventListener("aiIntelligenceChanged", handleAiIntelligenceChanged as EventListener);
    return () => {
      window.removeEventListener("aiIntelligenceChanged", handleAiIntelligenceChanged as EventListener);
    };
  }, [currentShop]);

  const handleOpenIsight = () => {
    const shop = currentShop || localStorage.getItem("shop");
    const token = localStorage.getItem("token");

    if (!shop) {
      setIsightError("Please select a store first.");
      return;
    }

    const url = `${ISIGHT_BASE_URL}/intelligence?shop=${encodeURIComponent(
      shop
    )}&netsight_token=${encodeURIComponent(token || "")}&theme=${getCurrentTheme()}`;
    window.open(url, "_blank", "noopener,noreferrer");

    // Mark that the user has successfully opened iSight at least once
    localStorage.setItem("isightClaimed", "true");
  };

  // const redirectToIsight = () => {
  //   if (isFreePlan) {
  //     try {
  //       const from = `${location.pathname}${location.search}${location.hash}`;
  //       sessionStorage.setItem("isightReturnTo", from);
  //     } catch {
  //       /* ignore */
  //     }
  //     navigate("/isights", { replace: false });
  //     return;
  //   }
  //   const shop = currentShop || localStorage.getItem("shop");
  //   const token = localStorage.getItem("token");
  //   const hasClaimed = localStorage.getItem("isightClaimed") === "true";

  //   // First-time (or not yet claimed) always routes to the iSights page
  //   if (!hasClaimed) {
  //     navigate("/isights", { replace: true });
  //     return;
  //   }

  //   // After the user has claimed once, go directly to iSight (shop + token)
  //   if (!shop) {
  //     setIsightError("Please select a store first.");
  //     return;
  //   }

  //   const url = `${ISIGHT_BASE_URL}/intelligence?shop=${encodeURIComponent(
  //     shop
  //   )}&netsight_token=${encodeURIComponent(token || "")}`;
  //   window.open(url, "_blank", "noopener,noreferrer");
  // };

  const redirectToIsight = () => {
    // if (isFreePlan) {
    //   // ... existing logic
    //   navigate("/isights", { replace: false });
    //   return;
    // }

    const shop = currentShop || localStorage.getItem("shop");
    const token = localStorage.getItem("token");

    // // Check credits from localStorage
    // const creditsRaw = localStorage.getItem("netsight_available_credits");
    // const credits = creditsRaw !== null ? Number(creditsRaw) : null;
    // const hasCredits = credits !== null && credits > 0;

    // // No credits → isights page
    // if (!hasCredits) {
    //   navigate("/isights", { replace: false });
    //   return;
    // }

    // // Has credits → direct to iSight
    if (!shop) {
      setIsightError("Please select a store first.");
      return;
    }

    const url = `${ISIGHT_BASE_URL}/intelligence?shop=${encodeURIComponent(shop)}&netsight_token=${encodeURIComponent(token || "")}&theme=${getCurrentTheme()}`;
    window.open(url, "_blank", "noopener,noreferrer");
    localStorage.setItem("isightClaimed", "true");
  };

  if (!sightsEnabled || !aiIntelligenceEnabled) {
    return null;
  }

  if (variant === "header") {
    return (
      <div className="flex justify-center items-center ">
        <button
          id="isight-open-btn"
          onClick={redirectToIsight}
          title={`Open iSight Intelligence for ${currentShop || "your store"}`}
          className={`menu-item group w-full flex items-center ${loading
              ? "menu-item-active"
              : "menu-item-inactive"
            } ${!expanded ? "lg:justify-start" : "justify-start px-3"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
        >

          {expanded ? (
            <>
              {/* Scalekit wordmark — shown like the iSight logo */}
              <span className="flex min-w-0  items-center">
                {loading ? (
                  <span className="block h-5 w-5 animate-spin rounded-full border-2 border-[var(--accent)] border-t-transparent" />
                ) : (
                  <>
                    <img
                      // src="/images/isightlogo/isight-white.svg"
                      src="https://app.netsights.ai/images/logo/isightwhite.svg"
                      alt="iSight"
                      className="w-20 object-contain hidden dark:block"
                    />
                    <img
                      // src="/images/isightlogo/isight-black.svg"
                      src="https://app.netsights.ai/images/logo/isightblack.svg"
                      alt="iSight"
                      className="w-20 object-contain dark:hidden"
                    />
                  </>
                )}
              </span>
              <span className="flex shrink-0 items-center">
                {loading ? (
                  <span className="whitespace-nowrap text-xs font-semibold text-[var(--accent)]">
                    Opening…
                  </span>
                ) : (
                  <sup className="tag-sup tag-sup--free shrink-0 rounded-full bg-[#9B7BFF]/15 px-2 py-2 text-[10px] font-bold uppercase tracking-wide text-[#3e9e58] dark:text-[#9B7BFF]">
                    Free
                  </sup>
                )}
              </span>
            </>
          ) : (
            <span className="menu-item-icon-size transition-transform duration-200 group-hover:scale-110">
              {loading ? (
                <span className="block h-5 w-5 animate-spin rounded-full border-2 border-[var(--accent)] border-t-transparent" />
              ) : (
                <Layers className="h-5 w-5 text-[#A3A3A3] group-hover:text-[var(--accent)]" />
              )}
            </span>
          )}
        </button>

        {isightError && (
          <div
            className="absolute top-full mt-2 z-50 px-3 py-2 rounded-lg text-xs text-white shadow-lg bg-red-600"
            style={{ minWidth: "200px" }}
          >
            {isightError}
            <button onClick={() => setIsightError(null)} className="ml-2 opacity-70 hover:opacity-100">
              ✕
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
      <div className="flex justify-center items-center ">
        <button
          id="isight-open-btn"
          onClick={redirectToIsight}
          title={`Open iSight Intelligence for ${currentShop || "your store"}`}
          className={`menu-item group w-full flex items-center ${loading
              ? "menu-item-active"
              : "menu-item-inactive"
            } ${!expanded ? "lg:justify-start" : "justify-start px-3"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
        >

          {expanded ? (
            <>
              {label ? (
                <>
                  <span className="menu-item-icon-size transition-transform duration-200 group-hover:scale-110">
                    {/* <Layers className="h-5 w-5 text-[#A3A3A3] group-hover:text-[var(--accent)]" /> */}
                    <Sparkles className="w-5 h-5 text-[#A3A3A3]" />
                  </span>
                  <span className="flex-1 truncate text-left">{label}</span>
                </>
              ) : (
                <span className="flex min-w-0  items-center">
                  {loading ? (
                    <span className="block h-5 w-5 animate-spin rounded-full border-2 border-[var(--accent)] border-t-transparent" />
                  ) : (
                    <>
                      <img
                        // src="/images/isightlogo/isight-white.svg"
                        src="https://app.netsights.ai/images/logo/isightwhite.svg"
                        alt="iSight"
                        className="w-20 object-contain dark:hidden"
                        style={{ height: 28, width: "auto", maxWidth: "100%" }}
                      />
                      <img
                        // src="/images/isightlogo/isight-black.svg"
                        src="https://app.netsights.ai/images/logo/isightblack.svg"
                        alt="iSight"
                        className="w-20  hidden object-contain dark:block"
                        style={{ height: 28, width: "auto", maxWidth: "100%" }}
                      />
                    </>
                  )}
                </span>
              )}
            </>
          ) : (
            <span className="menu-item-icon-size transition-transform duration-200 group-hover:scale-110">
              {loading ? (
                <span className="block h-5 w-5 animate-spin rounded-full border-2 border-[var(--accent)] border-t-transparent" />
              ) : (
                <Layers className="h-5 w-5 text-[#A3A3A3] group-hover:text-[var(--accent)]" />
              )}
            </span>
          )}
        </button>

        {isightError && (
          <div
            className="absolute top-full mt-2 z-50 px-3 py-2 rounded-lg text-xs text-white shadow-lg bg-red-600"
            style={{ minWidth: "200px" }}
          >
            {isightError}
            <button onClick={() => setIsightError(null)} className="ml-2 opacity-70 hover:opacity-100">
              ✕
            </button>
          </div>
        )}
      </div>
    );
}

export default IsightOpenButton;